import { css } from "@emotion/react"
import { CurrencyInput } from "source/giving/components/CurrencyInput"
import { Field } from "source/giving/components/Field"
import { Icon } from "source/shared/components"
import { BLANK_FUND_ID } from "../constants"
import { extractMessagesFrom } from "../stores/validations"
import { Designation, Fund, ValidationErrors } from "../types"

type DesignationRowProps = Designation & {
  errors: ValidationErrors
  funds: Fund[]
  index: number
  onFundChange: (fundId: string) => void
  onAmountChange: (amount: string) => void
  onRemove: () => void
  onFundBlur?: () => void
  onAmountBlur?: () => void
}

export function DesignationRow({
  amount,
  errors: validationErrors,
  fundId,
  funds,
  index,
  onFundChange,
  onAmountChange,
  onRemove,
  onFundBlur,
  onAmountBlur,
}: DesignationRowProps) {
  const FIELD_NAMES = {
    FUND: `designation-${index}-fund`,
    AMOUNT: `designation-${index}-amount`,
  }
  const humanIndex = index + 1

  const fieldsetErrors = extractMessagesFrom(
    validationErrors,
    `designation_${fundId}`,
  )
  const fundErrors = extractMessagesFrom(
    validationErrors,
    `designation_${fundId}_fund`,
  )
  const amountErrors = extractMessagesFrom(
    validationErrors,
    `designation_${fundId}_amount`,
  )

  return (
    <Field errors={fieldsetErrors}>
      {({ id, isValid, errorId }) => (
        <fieldset
          id={id}
          className="d-f ai-c g-1"
          aria-invalid={!isValid}
          aria-describedby={errorId}
        >
          <legend className="visually-hidden">
            Fund designation {humanIndex}
          </legend>
          <label className="visually-hidden" htmlFor={FIELD_NAMES.FUND}>
            Fund {humanIndex}
          </label>
          <select
            className="select f-2"
            id={FIELD_NAMES.FUND}
            value={fundId}
            onChange={(e) => onFundChange(e.target.value)}
            onBlur={onFundBlur}
            aria-invalid={fundErrors.length > 0}
          >
            {fundId === BLANK_FUND_ID && (
              <option value="">Choose a fund</option>
            )}
            {funds.map((fund) => (
              <option key={fund.id} value={fund.id}>
                {fund.name}
              </option>
            ))}
          </select>

          <label className="visually-hidden" htmlFor={FIELD_NAMES.AMOUNT}>
            Amount for fund {humanIndex}
          </label>
          <CurrencyInput
            className="f-1"
            id={FIELD_NAMES.AMOUNT}
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            onBlur={onAmountBlur}
            aria-invalid={amountErrors.length > 0}
          />

          <button
            className="btn minor-btn secondary-btn destroy-btn p-0"
            css={css`
              aspect-ratio: 1;
            `}
            type="button"
            onClick={onRemove}
            aria-label={`Remove fund ${humanIndex}}`}
          >
            <Icon symbol="general#x" aria-hidden="true" />
          </button>
        </fieldset>
      )}
    </Field>
  )
}
